import React from 'react';
import { defineMessages } from 'react-intl';
import { generatePath, useNavigate } from 'react-router-dom';
import { LoginOutlined, SaveOutlined, StopOutlined, UserOutlined } from '@ant-design/icons';
import BaseModal from '@components/common/form/BaseModal';
import ListPage from '@components/common/layout/ListPage';
import PageWrapper from '@components/common/layout/PageWrapper';
import { appAccount, AppConstants, DEFAULT_TABLE_ITEM_SIZE_LARGE, EMPTY_DETAIL, grantTypeShopAccount, P_CODE_GRANT_TYPE_SHOP_ACCOUNT, UserTypes } from '@constants';
import apiConfig from '@constants/apiConfig';
import { FieldTypes } from '@constants/formConfig';
import { statusOptions } from '@constants/masterData';
import useAuth from '@hooks/useAuth';
import useFetch from '@hooks/useFetch';
import useListModal from '@hooks/useListModal';
import useTranslate from '@hooks/useTranslate';
import { AvatarField, BaseTable, BaseTooltip } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import routes from '@routes';
import { showErrorMessage } from '@services/notifyService';
import { getCacheAccessToken, removeCacheAccessToken } from '@services/userService';
import { Button, Flex } from 'antd';

import ShopAccountForm from './ShopAccountForm';

const message = defineMessages({
    objectName: 'Shop Account',
    loginFail: 'Login failed!',
});

const FORM_ID = 'form-shop-account';

const ShopAccountListPage = () => {
    const translate = useTranslate();
    const statusValues = translate.formatKeys(statusOptions, ['label']);
    const navigate = useNavigate();
    const accessToken = getCacheAccessToken();
    const { pcodes } = useAuth();

    const base64Credentials = Buffer.from(`${appAccount.APP_USERNAME}:${appAccount.APP_PASSWORD}`).toString('base64');
    const { execute, loading: loadingLogin } = useFetch({
        ...apiConfig.account.loginBasic,
        authorization: `Basic ${base64Credentials}`,
    });

    const handleLogin = (id) => {
        execute({
            data: {
                token: accessToken,
                shopAccountId: id,
                grant_type: grantTypeShopAccount,
            },
            onCompleted: (res) => {
                navigate(routes.verify.path + `?accessToken=${res?.access_token}`);
            },
            onError: () => showErrorMessage(translate.formatMessage(message.loginFail), translate),
        });
    };
    
    const {
        data,
        mixinFuncs,
        queryFilter,
        pagination,
        loading,
        detail,
        loadingSave,
        openModal,
        onCancelModal,
        setIsChangedFormValues,
        onSave,
        isEditing,
        isChanged,
        titleModal,
    } = useListModal({
        apiConfig: {
            getList: apiConfig.shopAccount.getList,
            getById: apiConfig.shopAccount.getById,
            create: apiConfig.shopAccount.create,
            update: apiConfig.shopAccount.update,
            delete: apiConfig.shopAccount.delete,
            changeStatus: apiConfig.shopAccount.update,
        },
        options: {
            pageSize: DEFAULT_TABLE_ITEM_SIZE_LARGE,
            objectName: translate.formatMessage(message.objectName),
        },
        override: (funcs) => {
            funcs.mappingData = (response) => {
                if (response.result === true) {
                    return {
                        data: response.data.content,
                        total: response.data.totalElements,
                    };
                }
            };
            funcs.getList = () => {
                const params = {
                    ...queryFilter,
                    page: pagination.current - 1,
                    size: pagination.pageSize,
                    kind: UserTypes.SHOP,
                };
                return mixinFuncs.handleFetchList(params);
            };
            funcs.additionalActionColumnButtons = () => ({
                login: ({ id }) => (
                    <BaseTooltip title={translate.formatMessage(commonMessage.login)}>
                        <Button
                            type="link"
                            style={{ padding: 0 }}
                            disabled={loadingLogin || !(pcodes?.includes(P_CODE_GRANT_TYPE_SHOP_ACCOUNT))}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleLogin(id);
                            }}
                        >
                            <LoginOutlined />
                        </Button>
                    </BaseTooltip>
                ),
            });
        },
    });

    const handleSubmitForm = (values) => {
        const preparedData = isEditing
            ? { ...values, id: detail?.id }
            : { ...values, kind: UserTypes.SHOP, groupId: values.groupId };
        onSave(preparedData);
    };

    const columns = [
        {
            title: '#',
            dataIndex: 'logoPath',
            align: 'center',
            width: 100,
            render: (logoPath) => (
                <AvatarField
                    size="large"
                    icon={<UserOutlined />}
                    src={logoPath ? `${AppConstants.contentRootUrl}${logoPath}` : null}
                />
            ),
        },
        {
            title: translate.formatMessage(commonMessage.fullName),
            dataIndex: ['fullName'],
            render: (username, record) => {
                const hasPerm = mixinFuncs.hasPermission([apiConfig.shopAccount.getList.baseURL]);
                return hasPerm ? (
                    <BaseTooltip title={username}>
                        <Button
                            type="link"
                            style={{ padding: 0 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(
                                    generatePath(routes.RestaurantByCustomerListPage.path, { customerId: record?.id }),
                                );
                            }}
                        >
                            {username}
                        </Button>
                    </BaseTooltip>
                ) : (
                    <span>{username}</span>
                );
            },
        },
        {
            title: translate.formatMessage(commonMessage.username),
            dataIndex: ['username'],
            width: '240px',
        },
        {
            title: translate.formatMessage(commonMessage.phone),
            dataIndex: 'phone',
            width: '200px',
        },
        {
            title: translate.formatMessage(commonMessage.email),
            dataIndex: ['email'],
            width: '200px',
        },

        mixinFuncs.renderStatusColumn({ width: '120px' }),
        mixinFuncs.renderActionColumn(
            {
                // login: true,
                edit: true,
                delete: true,
            },
            { width: '120px' },
        ),
    ];

    const searchFields = [
        {
            key: 'username',
            placeholder: translate.formatMessage(commonMessage.username),
        },
        {
            key: 'fullName',
            placeholder: translate.formatMessage(commonMessage.fullName),
        },
        {
            key: 'status',
            placeholder: translate.formatMessage(commonMessage.statusSelect),
            type: FieldTypes.SELECT,
            options: statusValues,
        },
    ];

    return (
        <PageWrapper routes={[{ breadcrumbName: translate.formatMessage(commonMessage.shopAccount) }]}>
            <ListPage
                searchForm={mixinFuncs.renderSearchForm({ fields: searchFields, initialValues: queryFilter })}
                actionBar={mixinFuncs.renderActionBar()}
                baseTable={
                    <BaseTable
                        onChange={mixinFuncs.changePagination}
                        columns={columns}
                        dataSource={data}
                        pagination={pagination}
                        loading={loading}
                    />
                }
            />
            {openModal && (
                <BaseModal
                    isModalVisible={openModal}
                    isEditing={isEditing}
                    handleCancel={onCancelModal}
                    title={titleModal}
                    width="800px"
                    noBodyPadding={true}
                    customFooter={
                        <Flex justify="end" gap={8} style={{ padding: '8px 16px' }}>
                            <Button danger onClick={onCancelModal} icon={<StopOutlined />}>
                                {translate.formatMessage(commonMessage.cancel)}
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                form={FORM_ID}
                                loading={loadingSave}
                                disabled={isEditing && !isChanged}
                                icon={<SaveOutlined />}
                            >
                                {translate.formatMessage(isEditing ? commonMessage.update : commonMessage.save)}
                            </Button>
                        </Flex>
                    }
                >
                    <ShopAccountForm
                        formId={FORM_ID}
                        dataDetail={detail || EMPTY_DETAIL}
                        isEditing={isEditing}
                        actions={null}
                        onSubmit={handleSubmitForm}
                        setIsChangedFormValues={setIsChangedFormValues}
                    />
                </BaseModal>
            )}
        </PageWrapper>
    );
};

export default ShopAccountListPage;
